import React, { useState, useEffect } from 'react';
import { Plus, Trash2, RefreshCw } from 'lucide-react';
import { fetchPackingCostConfig, savePackingCostConfig, refreshPackingCostsFromWos, computePackingCost } from '../services/api';

const normalize = (cfg = {}) => ({
  boxes: Array.isArray(cfg.boxes)
    ? cfg.boxes.map(b => ({ boxType: b.boxType || '', wosItem: b.wosItem || '', cost: Number(b.cost ?? 0) }))
    : [],
  gel: { caseCost: Number(cfg.gel?.caseCost ?? 0), perCase: Number(cfg.gel?.perCase ?? 36), wosItem: cfg.gel?.wosItem || 'CP200' },
  dryIce: { perLb: Number(cfg.dryIce?.perLb ?? 0) },
});

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function PackingCosts() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchPackingCostConfig()
      .then(cfg => setConfig(normalize(cfg)))
      .catch(e => setStatus({ type: 'error', msg: 'Could not load: ' + e.message }));
  }, []);

  const upd = (fn) => {
    setConfig(c => { const next = JSON.parse(JSON.stringify(c)); fn(next); return next; });
    setDirty(true);
    setStatus(null);
  };

  const save = async () => {
    setSaving(true); setStatus(null);
    try {
      await savePackingCostConfig(config);
      setDirty(false);
      setStatus({ type: 'ok', msg: 'Saved.' });
    } catch (e) {
      setStatus({ type: 'error', msg: 'Save failed: ' + e.message });
    } finally { setSaving(false); }
  };

  const refreshFromWos = async () => {
    setRefreshing(true); setStatus(null);
    try {
      const next = await refreshPackingCostsFromWos(config);
      setConfig(normalize(next));
      setDirty(true);
      setStatus({ type: 'ok', msg: 'Pulled box + gel costs from WOS. Review and Save.' });
    } catch (e) {
      setStatus({ type: 'error', msg: 'WOS refresh failed: ' + e.message });
    } finally { setRefreshing(false); }
  };

  if (!config) {
    return (
      <div>
        <div className="page-header">Packing Costs</div>
        <div style={{ padding: 24, color: 'var(--gray-500)', fontSize: 13 }}>{status?.msg || 'Loading…'}</div>
      </div>
    );
  }

  const perGel = config.gel.perCase ? config.gel.caseCost / config.gel.perCase : 0;
  // Live example so Greg can sanity-check the math.
  const example = computePackingCost(config, { boxType: 'CP30', gelPacks: 9, dryIce: 5 });

  const inputStyle = { width: 90, textAlign: 'right', border: '1px solid var(--gray-300)', borderRadius: 4, padding: '4px 6px', fontFamily: 'Montserrat', fontSize: 13 };

  return (
    <div>
      <div className="breadcrumb"><span>Fulfillment</span><span>›</span><span>Packing Costs</span></div>
      <div className="page-header">Packing Costs</div>
      <p style={{ fontSize: 13, color: 'var(--gray-600)', maxWidth: 760, marginBottom: 16 }}>
        The per-unit costs used to compute profit-per-box in the Order Profit view. Box and gel costs pull from
        WOS (WarehouseOS) <strong>automatically every hour</strong> (only when a price actually changes) — or hit the
        button below to force it now; dry ice has no per-lb figure in WOS, so set it here. Packing cost = <strong>box + (gel count × per-gel) + (dry ice lbs × per-lb)</strong>.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn btn-outline" onClick={refreshFromWos} disabled={refreshing} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> {refreshing ? 'Pulling…' : 'Refresh box + gel costs from WOS'}
        </button>
        <button className="btn btn-navy" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}</button>
        {status && <span style={{ fontSize: 12, fontWeight: 600, color: status.type === 'error' ? '#c0392b' : '#1e7e34' }}>{status.msg}</span>}
      </div>

      {/* Boxes */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">Box Types (Coolers)</div>
        <div className="card-body">
          <table className="product-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Box Type</th>
                <th>WOS Item</th>
                <th style={{ textAlign: 'right' }}>Cost / box</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {config.boxes.map((b, i) => (
                <tr key={i}>
                  <td>
                    <input value={b.boxType} onChange={e => upd(c => { c.boxes[i].boxType = e.target.value.toUpperCase(); })}
                      style={{ ...inputStyle, width: 90, textAlign: 'left' }} />
                  </td>
                  <td>
                    <input value={b.wosItem} onChange={e => upd(c => { c.boxes[i].wosItem = e.target.value.toUpperCase(); })}
                      style={{ ...inputStyle, width: 90, textAlign: 'left' }} placeholder="e.g. CP30" />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span style={{ color: 'var(--gray-500)' }}>$</span>
                    <input type="number" step="0.01" value={b.cost} onChange={e => upd(c => { c.boxes[i].cost = e.target.value; })} style={inputStyle} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="btn-ghost" title="Remove" onClick={() => upd(c => { c.boxes.splice(i, 1); })} style={{ color: 'var(--gray-400)', border: 'none', background: 'none', cursor: 'pointer' }}>
                      <Trash2 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-ghost" onClick={() => upd(c => { c.boxes.push({ boxType: '', wosItem: '', cost: 0 }); })} style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <Plus size={14} /> Add box type
          </button>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8 }}>
            Box type matches the order's box exactly (e.g. <code>CP30</code>). Note WOS also has <code>CP030</code> (King Crab) — that's a different item. "NO BOX" costs $0.
          </div>
        </div>
      </div>

      {/* Gel + Dry Ice */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header">Gel Packs</div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Case cost <span style={{ color: 'var(--gray-400)' }}>(WOS {config.gel.wosItem})</span></label>
              <div><span style={{ color: 'var(--gray-500)' }}>$</span>
                <input type="number" step="0.01" value={config.gel.caseCost} onChange={e => upd(c => { c.gel.caseCost = e.target.value; })} style={inputStyle} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Gels per case</label>
              <input type="number" step="1" value={config.gel.perCase} onChange={e => upd(c => { c.gel.perCase = e.target.value; })} style={{ ...inputStyle, textAlign: 'left', width: 80 }} />
            </div>
            <div style={{ fontSize: 13, marginTop: 8, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6 }}>
              Per gel = <strong>{money(perGel)}</strong> <span style={{ color: 'var(--gray-500)' }}>({money(config.gel.caseCost)} ÷ {config.gel.perCase || 0})</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Dry Ice</div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Cost per lb <span style={{ color: 'var(--gray-400)' }}>(not in WOS — set manually)</span></label>
              <div><span style={{ color: 'var(--gray-500)' }}>$</span>
                <input type="number" step="0.01" value={config.dryIce.perLb} onChange={e => upd(c => { c.dryIce.perLb = e.target.value; })} style={inputStyle} />
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8 }}>
              The order records dry ice in pounds. Cost = lbs × this rate.
            </div>
          </div>
        </div>
      </div>

      {/* Example */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">Example</div>
        <div className="card-body" style={{ fontSize: 13 }}>
          A <strong>CP30</strong> box + <strong>9</strong> gel packs + <strong>5 lb</strong> dry ice ={' '}
          {money(example.box)} + {money(example.gel)} + {money(example.dryice)} = <strong style={{ color: 'var(--orange)' }}>{money(example.total)}</strong> packing cost.
        </div>
      </div>
    </div>
  );
}
